import React, { useEffect, useMemo } from 'react';

import type { IntlShape } from 'react-intl-next';
import { useIntl } from 'react-intl-next';

import { aiExperienceApplicationMessages } from '@atlaskit/editor-common/ai-messages';
import { ACTION } from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import type { StreamFlowPacer } from '@atlassian/editor-ai-common/api/stream-flow-pacer';
import { DiscardScreen } from '@atlassian/generative-ai-modal/screens/Discard';

import type { AIPlugin } from '../../../../aiPluginType';
import { type StreamFns } from '../../../../pm-plugins/in-editor-direct-streaming/hooks/useInEditorDirectStreaming';
import { useAIExperienceCommonDataContext } from '../../../hooks/useAIExperienceCommonData';
import { useRovoAbort } from '../../../hooks/useRovoDiscard';
import { convertExperienceName } from '../../../utils/analytics/analyticsFlow';
import { useAnalyticsFlowEventsQueue } from '../../../utils/analytics/analyticsFlowUtils';
import { useFireAIAnalyticsEvent } from '../../../utils/analytics/utils';
import type {
	AIExperienceMachineContext,
	HistoryEvent,
	LatestPrompt,
} from '../../../utils/xstate/get-ai-experience-service';

export const getDiscardMessage = ({
	formatMessage,
	experienceState,
}: {
	experienceState: string | Record<string, string>;
	formatMessage: IntlShape['formatMessage'];
}): string => {
	const discardPrompt = formatMessage(aiExperienceApplicationMessages.discardMessagePrompt);
	const discardResponse = formatMessage(aiExperienceApplicationMessages.discardMessageResponse);
	const discardStreamingInDocumentResponse = formatMessage(
		aiExperienceApplicationMessages.discardStreamingInDocumennt,
	);

	if (typeof experienceState === 'object' && 'user input state' in experienceState) {
		return discardPrompt;
	}

	if (
		experienceState === 'review state' ||
		(typeof experienceState === 'object' && 'loading state' in experienceState)
	) {
		return discardStreamingInDocumentResponse;
	}

	return discardResponse;
};

export function DiscardScreenWithLogic({
	state,
	editCount,
	retryPromptCount,
	history,
	onceDiscard,
	channelId,
	streamFlowPacer,
	discardStream,
	api,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	channelId?: string;
	discardStream?: StreamFns['discardStream'];
	editCount: number;
	history: {
		entries: HistoryEvent[];
		positionFromEnd: number;
		totalSize: number;
		traversals: number;
	};
	latestPrompt?: LatestPrompt;
	onceDiscard?: AIExperienceMachineContext['onceDiscard'];
	retryPromptCount: number;
	state: string | Record<string, string>;
	streamFlowPacer: StreamFlowPacer | null;
}): React.JSX.Element | null {
	const aiExperienceCommonData = useAIExperienceCommonDataContext();
	const nextSessionId = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) => aiExperienceState?.aiDecorationExperiencePluginState.nextSessionId,
	);
	const {
		endExperience,
		sendToAIExperienceMachine,
		configItem,
		includeKnowledgeFromCurrentPage,
		appearance,
	} = aiExperienceCommonData;

	const inputOutputDiffRatio = useMemo(() => {
		const lastHistoryEvent = history.entries[history.positionFromEnd];
		return history.entries.length === 0 ? '' : lastHistoryEvent.inputOutputDiffRatio;
	}, [history]);

	const { formatMessage } = useIntl();
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const analyticsFlow = useAnalyticsFlowEventsQueue({
		stepName: 'discard',
		attributes: {
			editCount,
			retryPromptCount,
			inputOutputDiffRatio,
			includeKnowledgeFromCurrentPage,
		},
	});

	useEffect(() => {
		streamFlowPacer?.pause();
		return () => {
			streamFlowPacer?.resume();
		};
	}, [streamFlowPacer]);

	const { abort } = useRovoAbort();

	// If a session is active - we'll show the loading screen elsewhere and we can hide this
	if (nextSessionId) {
		return null;
	}

	return (
		<DiscardScreen
			text={getDiscardMessage({
				formatMessage,
				experienceState: state,
			})}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onCancel={function () {
				sendToAIExperienceMachine({ type: 'disable discard' });
			}}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onDiscard={function (): void {
				/**
				 * Introduced in EDF-1443 to track discard event confirmations
				 * separately to normal discard events which get tracked
				 * earlier in this file
				 */
				fireAIAnalyticsEvent({
					payload: createUnifiedAnalyticsPayload({
						action: ACTION.DISMISSED,
						interactionID: channelId ?? analyticsFlow.getLastAiInteractionId(),
						experienceName: convertExperienceName(configItem.key.toString()),
						isProactive: false,
						attributes: analyticsFlow.getAiLifeCycleDynamicAttributes({ init: false }),
						invokedFrom: aiExperienceCommonData.lastTriggeredFrom,
						traceIds: fg('platform_editor_ai_traceid_in_analytics_events')
							? analyticsFlow.getCommonAttributes().traceIds
							: undefined,
					}),
				});

				// Abort any ongoing Rovo inline input box operations when discarding
				// Note if Rovo is not being used this will be a no-op
				abort();

				if (onceDiscard) {
					sendToAIExperienceMachine({ type: 'disable discard' });
					onceDiscard();
				} else {
					discardStream?.();
					endExperience();
				}
			}}
			appearance={appearance}
			isAIFC
		/>
	);
}
